import React from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RsvpToggle from './RsvpToggle';
import { formatHomeDate, formatHomeTime, getRelativeDayLabel, getSpecialGameTypeLabel } from './homeAgenda';

const displayOnlyChipProps = { size: 'small', clickable: false, onClick: () => { } };
const tagSx = { height: 22, fontSize: '0.75rem' };

const LeagueTag = ({ name, slug }) => {
  if (!name) return null;
  if (!slug) return <Chip label={name} sx={tagSx} {...displayOnlyChipProps} />;
  return (
    <Chip
      label={name}
      size="small"
      component={RouterLink}
      to={`/leagues/${slug}/standings`}
      clickable
      sx={tagSx}
    />
  );
};

const TeamLink = ({ name, slug }) => (slug ? (
  <Box component={RouterLink} to={`/teams/${slug}`} sx={{ color: 'primary.main' }}>{name}</Box>
) : name);

const renderTitle = (item) => {
  if (item.kind === 'game') {
    if (!item.playingAgainstTeamName) {
      return <><TeamLink name={item.playingForTeamName} slug={item.playingForTeamSlug} /> · Bye week</>;
    }
    return (
      <>
        <TeamLink name={item.playingForTeamName} slug={item.playingForTeamSlug} />
        {' vs '}
        <TeamLink name={item.playingAgainstTeamName} slug={item.playingAgainstTeamSlug} />
      </>
    );
  }
  if (item.kind === 'spare') return item.leagueName;
  return item.title;
};

const renderSubtitle = (item) => {
  if (item.kind === 'spare') return 'Spare availability';
  if (item.kind === 'social') return item.location;
  return [item.venueName, item.fieldName].filter(Boolean).join(' · ');
};

const renderTags = (item, leagueSlugById) => {
  const tags = [];
  const special = item.kind !== 'social' ? getSpecialGameTypeLabel(item.gameType) : null;
  if (special) tags.push(<Chip key="type" label={special} color="warning" variant="outlined" sx={tagSx} {...displayOnlyChipProps} />);
  if (item.kind === 'spare') tags.push(<Chip key="spare" label="Spare" sx={tagSx} {...displayOnlyChipProps} />);
  if (item.kind === 'game') tags.push(<LeagueTag key="league" name={item.leagueName} slug={item.leagueSlug} />);
  if (item.kind === 'social') {
    if (item.isTenantWide) tags.push(<Chip key="org" label="Org-wide" sx={tagSx} {...displayOnlyChipProps} />);
    else if (item.teamName) tags.push(<Chip key="team" label={item.teamName} sx={tagSx} {...displayOnlyChipProps} />);
    else if (item.leagueName) tags.push(<LeagueTag key="league" name={item.leagueName} slug={leagueSlugById[item.leagueId]} />);
  }
  return tags;
};

const AgendaItem = ({ item, rsvp, onRsvp, canRsvp, leagueSlugById }) => {
  const subtitle = renderSubtitle(item);
  const attending = rsvp?.attending ?? item.attending ?? null;

  return (
    <Box
      data-testid={`agenda-item-${item.key}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '4px minmax(0, 1fr)', sm: '72px 4px minmax(0, 1fr) auto' },
        gridTemplateAreas: {
          xs: '"bar time" "bar main" "bar rsvp"',
          sm: '"time bar main rsvp"',
        },
        columnGap: 1.5,
        rowGap: 0.5,
        alignItems: 'center',
        px: { xs: 1.75, sm: 2.5 },
        py: 1.25,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ gridArea: 'time', fontWeight: { xs: 600, sm: 400 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
      >
        {formatHomeTime(item.start)}
      </Typography>
      <Box
        sx={{
          gridArea: 'bar',
          alignSelf: 'stretch',
          borderRadius: 1,
          bgcolor: item.kind === 'social' ? 'warning.main' : 'primary.main',
        }}
      />
      <Box sx={{ gridArea: 'main', minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body1" component="span" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {renderTitle(item)}
          </Typography>
          {renderTags(item, leagueSlugById)}
        </Stack>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ gridArea: 'rsvp', mt: { xs: 0.5, sm: 0 } }}>
        {canRsvp ? (
          <RsvpToggle
            value={attending}
            disabled={!!rsvp?.submitting}
            error={rsvp?.error}
            onChange={(next) => onRsvp(item, next)}
            yesLabel={item.kind === 'spare' ? 'Available' : 'Going'}
            ariaLabel={`Attendance for ${item.kind === 'game' ? 'game' : item.kind === 'spare' ? 'spare slot' : item.title}`}
          />
        ) : item.availabilityLink ? (
          <Button component={RouterLink} to={item.availabilityLink} variant="outlined" size="small">
            Set availability
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

/** Day-grouped list of upcoming games, spare slots and social events. */
const ComingUpAgenda = ({ groups, rsvpState, onRsvp, canRsvp, leagueSlugById = {}, today }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" component="h2" className="section-title">
      Coming up
    </Typography>
    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
      {[{ label: 'Games', color: 'primary.main' }, { label: 'Social events', color: 'warning.main' }].map((entry) => (
        <Stack key={entry.label} direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: entry.color }} />
          <Typography variant="caption" color="text.secondary">{entry.label}</Typography>
        </Stack>
      ))}
    </Stack>
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, overflow: 'hidden' }}>
      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
          Nothing coming up right now.
        </Typography>
      ) : groups.map((group, index) => (
        <Box key={group.key} component="section" aria-label={formatHomeDate(group.date)}>
          {index > 0 && <Divider />}
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ px: { xs: 1.75, sm: 2.5 }, pt: 1.5, pb: 0.5 }}>
            <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {formatHomeDate(group.date)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getRelativeDayLabel(group.date, today)}
            </Typography>
          </Stack>
          {group.items.map((item) => (
            <AgendaItem
              key={item.key}
              item={item}
              rsvp={rsvpState[item.key]}
              onRsvp={onRsvp}
              canRsvp={canRsvp(item)}
              leagueSlugById={leagueSlugById}
            />
          ))}
        </Box>
      ))}
    </Box>
  </Box>
);

export default ComingUpAgenda;
